import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface ContactFormProps {
  className?: string;
  showTitle?: boolean;
  title?: string;
}

export default function ContactForm({ className = "", showTitle = true, title }: ContactFormProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isArabic = i18n.language?.startsWith("ar");
  const cities = isArabic
    ? [
        "الرياض",
        "جدة",
        "الدمام",
        "الخبر",
        "مكة",
        "المدينة المنورة",
        "تبوك",
        "أبها",
        "الطائف",
        "حائل",
        "بريدة",
        "الخرج",
        "القطيف",
        "خميس مشيط",
        "الجبيل",
        "ينبع",
        "أخرى",
      ]
    : [
        "Riyadh",
        "Jeddah",
        "Dammam",
        "Khobar",
        "Makkah",
        "Madinah",
        "Tabuk",
        "Abha",
        "Taif",
        "Hail",
        "Buraidah",
        "Al Kharj",
        "Qatif",
        "Khamis Mushait",
        "Jubail",
        "Yanbu",
        "Other",
      ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());
    const endpointFromEnv = (import.meta.env.VITE_CONTACT_FORM_ENDPOINT || "").trim();
    const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").trim();
    const endpoint = endpointFromEnv || (apiBaseUrl
      ? `${apiBaseUrl.replace(/\/$/, "")}/mail_api/contact.php`
      : "/mail_api/contact.php");

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        navigate("/thank-you");
        form.reset();
      } else {
        setError(t("contact.formErrors.submit"));
      }
    } catch {
      setError(t("contact.formErrors.network"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={className}>
      {showTitle && (
        <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-6">
          {title || t("contact.formTitle")}
        </h3>
      )}
      <form
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-1.5">
            {isArabic ? "الاسم الكامل" : "Full Name"}
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            className="w-full px-4 py-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors"
            placeholder={isArabic ? "اكتب اسمك الكامل" : "Enter your full name"}
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-1.5">
            {isArabic ? "رقم الجوال" : "Phone Number"}
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            required
            pattern="[0-9+]*"
            className="w-full px-4 py-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors"
            placeholder={isArabic ? "05xxxxxxxx" : "+9665xxxxxxxx"}
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-1.5">
            {isArabic ? "البريد الإلكتروني" : "Email Address"}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            className="w-full px-4 py-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors"
            placeholder="example@domain.com"
          />
        </div>

        <div>
          <label htmlFor="city" className="block text-sm font-medium text-stone-700 mb-1.5">
            {isArabic ? "المدينة" : "City"}
          </label>
          <select
            id="city"
            name="city"
            required
            className="w-full px-4 py-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors bg-white"
          >
            <option value="">{isArabic ? "اختر المدينة" : "Choose city"}</option>
            {cities.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-1.5">
            {isArabic ? "الرسالة" : "Message"}
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            maxLength={500}
            required
            className="w-full px-4 py-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors resize-none"
            placeholder={isArabic ? "اكتب رسالتك أو استفسارك هنا..." : "Write your message or inquiry here..."}
          />
          <p className="text-xs text-stone-400 mt-1">{isArabic ? "الحد الأقصى 500 حرف" : "Maximum 500 characters"}</p>
        </div>

        <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 space-y-3">
          <p className="text-xs md:text-sm text-stone-600 leading-relaxed">
            {t("contact.dataNotice")}
          </p>

          <label htmlFor="policyConsent" className="flex items-start gap-2 cursor-pointer">
            <input
              id="policyConsent"
              name="policyConsent"
              type="checkbox"
              value="accepted"
              required
              className="mt-1 h-4 w-4 accent-brand"
            />
            <span className="text-xs md:text-sm text-stone-700 leading-relaxed">
              {t("contact.policyConsentStart")}{" "}
              <Link
                to="/privacy-policy"
                className="text-brand hover:text-brand-dark underline underline-offset-2"
              >
                {t("footer.privacy")}
              </Link>{" "}
              {t("contact.policyConsentAnd")}{" "}
              <Link
                to="/terms-of-service"
                className="text-brand hover:text-brand-dark underline underline-offset-2"
              >
                {t("footer.terms")}
              </Link>{" "}
              {t("contact.policyConsentEnd")}
            </span>
          </label>

          <input type="hidden" name="policyVersion" value="2026-05-09" />
          <input type="hidden" name="language" value={isArabic ? "ar" : "en"} />
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">{error}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold px-8 py-4 rounded-lg hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-lg" />
              {t("contact.submitLoading")}
            </>
          ) : (
            <>
              {t("contact.submitButton")}
              <span className="w-5 h-5 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-send-plane-line text-xs" />
              </span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}